import React, { useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_ENDPOINTS, buildApiUrl } from '../../config/apiConfig';
import AdminHubPageFrame from '../../components/admin/AdminHubPageFrame';
import AdminFilterBar from '../../components/admin/AdminFilterBar';
import AdminSectionHeader from '../../components/admin/AdminSectionHeader';
import AdminMetricCard from '../../components/admin/AdminMetricCard';
import AdminStatusBadge from '../../components/admin/AdminStatusBadge';
import AdminWarningList from '../../components/admin/AdminWarningList';
import AdminJsonDetails from '../../components/admin/AdminJsonDetails';

export default function AdminCommercialFirmPage() {
  const { t } = useLanguage();
  const [isGuest, setIsGuest] = useState(true);
  const [hasFirmJwt, setHasFirmJwt] = useState(false);
  const [officePlanCode, setOfficePlanCode] = useState('');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr(false);
    try {
      const q = new URLSearchParams();
      q.set('is_guest', isGuest ? 'true' : 'false');
      q.set('has_firm_jwt', hasFirmJwt ? 'true' : 'false');
      if (officePlanCode.trim()) q.set('office_plan_code', officePlanCode.trim());
      const res = await fetch(
        `${buildApiUrl(API_ENDPOINTS.ADMIN_COMMERCIAL_FIRM_ENTITLEMENT_PREVIEW)}?${q.toString()}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('bad');
      setData(await res.json());
    } catch {
      setErr(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const ent = data?.entitlement;
  const bridge = data?.officeBridge?.resolvedPlan;

  const warnings = useMemo(() => {
    if (!ent) return [];
    if (ent.firm_mode_enabled) {
      return [{ code: 'FIRM_EXEMPT', message: t('adminKit.firmTemporaryExemptionWarn'), tone: 'soft' }];
    }
    return [{ code: 'PREVIEW', message: t('adminKit.firmPreviewNoBehaviorChange'), tone: 'soft' }];
  }, [ent, t]);

  const crumbs = useMemo(
    () => [
      { label: t('adminHub.navCommercial'), to: '/admin/commercial/plans' },
      { label: t('adminHub.navFirmMode') },
    ],
    [t]
  );

  return (
    <AdminHubPageFrame titleKey="adminHub.firmModeTitle" descKey="adminHub.firmModeDesc" breadcrumbItems={crumbs}>
      <section className="admin-section-card">
        <AdminFilterBar>
          <label>
            <input type="checkbox" checked={isGuest} onChange={(e) => setIsGuest(e.target.checked)} />
            {t('adminHub.firmPreviewGuest')}
          </label>
          <label>
            <input type="checkbox" checked={hasFirmJwt} onChange={(e) => setHasFirmJwt(e.target.checked)} />
            {t('adminHub.firmPreviewJwt')}
          </label>
          <label>
            {t('adminHub.officePlanCode')}
            <input value={officePlanCode} onChange={(e) => setOfficePlanCode(e.target.value)} />
          </label>
          <button type="button" className="admin-hub-btn" onClick={load} disabled={loading}>
            {loading ? t('adminPage.loading') : t('adminHub.loadAction')}
          </button>
        </AdminFilterBar>
        {err ? (
          <div className="admin-state admin-state--error" role="status">
            <p>{t('adminHub.loadError')}</p>
          </div>
        ) : null}
        {data ? (
          <div className="admin-hub-kit-stack">
            <AdminWarningList items={warnings} />
            <AdminSectionHeader title={t('adminKit.firmEntitlementTitle')} subtitle={t('adminKit.firmEntitlementSub')} />
            <div className="admin-hub-kit-metric-grid">
              <AdminMetricCard
                label={t('adminKit.firmModeEnabled')}
                value={
                  <AdminStatusBadge tone={ent?.firm_mode_enabled ? 'ok' : 'neutral'}>
                    {ent?.firm_mode_enabled ? t('adminKit.yes') : t('adminKit.no')}
                  </AdminStatusBadge>
                }
              />
              <AdminMetricCard label={t('adminKit.firmModeSource')} value={ent?.firm_mode_source || '—'} />
              <AdminMetricCard
                label={t('adminKit.addonRequired')}
                value={
                  <AdminStatusBadge tone={ent?.addon_required ? 'warn' : 'neutral'}>
                    {ent?.addon_required ? t('adminKit.yes') : t('adminKit.no')}
                  </AdminStatusBadge>
                }
              />
              <AdminMetricCard label={t('adminKit.officePlanCodeLabel')} value={ent?.office_plan_code || '—'} />
              <AdminMetricCard label={t('adminKit.billingStatus')} value={ent?.billing_status || '—'} />
              <AdminMetricCard label={t('adminKit.enforcementMode')} value={ent?.enforcement || '—'} />
            </div>
            {bridge ? (
              <>
                <AdminSectionHeader title={t('adminKit.officePlanResolvedTitle')} level={3} />
                <div className="admin-hub-kit-metric-grid">
                  <AdminMetricCard label={t('adminKit.planCode')} value={bridge.planCode || '—'} />
                  <AdminMetricCard label={t('adminKit.resolverSource')} value={bridge.source || '—'} />
                  <AdminMetricCard label={t('adminKit.audienceType')} value={bridge.audience_type || '—'} />
                </div>
              </>
            ) : null}
            <AdminJsonDetails title={t('adminKit.rawJsonToggle')} data={data} />
          </div>
        ) : null}
      </section>
    </AdminHubPageFrame>
  );
}
